import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { AuthWordmark } from "./AuthWordmark";

type Intent = "individual" | "firm";

const DISCIPLINES = [
  { value: "structural", label: "Structural" },
  { value: "civil", label: "Civil" },
  { value: "mep", label: "MEP" },
  { value: "geotech", label: "Geotechnical" },
  { value: "architectural", label: "Architectural" },
  { value: "multi", label: "Multi-discipline" },
  { value: "other", label: "Other" },
];

const FIRM_SIZES = [
  { value: "1", label: "Just me" },
  { value: "2-10", label: "2–10" },
  { value: "11-50", label: "11–50" },
  { value: "50+", label: "50+" },
];

const STAGES = [
  { value: "concept", label: "Concept / schematic" },
  { value: "analysis", label: "Analysis & design" },
  { value: "calcs", label: "Calculations" },
  { value: "detailing", label: "Detailing / drafting" },
  { value: "qa", label: "QA / review" },
  { value: "permitting", label: "Permitting & stamping" },
  { value: "construction_admin", label: "Construction admin" },
];

const TOOLS = [
  "AutoCAD", "Revit", "Civil 3D", "SAP2000", "ETABS", "RISA",
  "STAAD", "RAM", "Tekla", "Bluebeam", "Excel",
];

const STEPS = ["You", "Your firm", "How you work"];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface BetaForm {
  contactName: string;
  email: string;
  role: string;
  phone: string;
  accountIntent: Intent;
  firmName: string;
  discipline: string;
  firmSize: string;
  seats: string;
  jurisdiction: string;
  website: string;
  stages: string[];
  tools: string[];
  otherTools: string;
  currentSop: string;
  bottleneck: string;
}

const EMPTY: BetaForm = {
  contactName: "", email: "", role: "", phone: "",
  accountIntent: "firm",
  firmName: "", discipline: "", firmSize: "", seats: "",
  jurisdiction: "", website: "",
  stages: [], tools: [], otherTools: "",
  currentSop: "", bottleneck: "",
};

export function BetaSignupPage() {
  // The landing CTAs carry intent: "Book a demo" (run a real plan live), the
  // beta waitlist, or "plans" (the done-for-you intake: send a PDF/DWG, get a
  // stamp-ready set back). Honored in the copy so each promise doesn't dead-end
  // on a generic form. (Display only — the submission payload is unchanged; a
  // plans request identifies itself in the project-description text.)
  const [searchParams] = useSearchParams();
  const funnel = searchParams.get("intent");
  const isDemo = funnel === "demo";
  const isPlans = funnel === "plans";
  const [form, setForm] = useState<BetaForm>(EMPTY);
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = <K extends keyof BetaForm>(k: K, v: BetaForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const toggle = (k: "stages" | "tools", v: string) =>
    setForm((f) => ({
      ...f,
      [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v],
    }));

  const isFirm = form.accountIntent === "firm";
  const isLast = step === STEPS.length - 1;

  // Only the first step gates progress — the firm details and qualitative
  // sections are optional so the funnel doesn't bleed completions.
  const stepError = (): string => {
    if (!form.contactName.trim()) return "Please tell us your name.";
    if (!EMAIL_RE.test(form.email.trim())) return "Please enter a valid work email.";
    return "";
  };

  const advance = () => {
    if (step === 0) {
      const err = stepError();
      if (err) { setError(err); return; }
    }
    setError("");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLast) { advance(); return; }

    const err = stepError();
    if (err) { setStep(0); setError(err); return; }

    setError("");
    setLoading(true);
    try {
      const extraTools = form.otherTools
        .split(",").map((t) => t.trim()).filter(Boolean);
      // Public endpoint — no auth token. Idempotent on email server-side.
      await api.post("/api/beta/signup", {
        contactName: form.contactName.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role.trim() || null,
        phone: form.phone.trim() || null,
        accountIntent: form.accountIntent,
        firmName: form.firmName.trim() || null,
        discipline: form.discipline || null,
        firmSize: isFirm ? form.firmSize || null : "1",
        seats: isFirm ? (form.seats ? Number(form.seats) : null) : 1,
        jurisdiction: form.jurisdiction.trim() || null,
        website: form.website.trim() || null,
        stages: form.stages,
        tools: [...form.tools, ...extraTools],
        currentSop: form.currentSop.trim() || null,
        bottleneck: form.bottleneck.trim() || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card-wrap is-wide">
        <AuthWordmark />

        <div className="card">
          <div className="card-body beta-wizard auth-stack">
            {submitted ? (
              <div className="signup-success">
                <div className="signup-success-icon">
                  <svg viewBox="0 0 24 24" fill="none" style={{ width: 24, height: 24 }}>
                    <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div className="auth-title">
                  {isPlans ? "Intake started" : isDemo ? "Demo requested" : "You're on the list"}
                </div>
                <p className="auth-sub">
                  Thanks{form.contactName ? `, ${form.contactName.split(" ")[0]}` : ""}. We've got your
                  request. We'll reach out at <strong>{form.email}</strong>{" "}
                  {isPlans
                    ? "with where to send your PDF or DWG. The stamp-ready set typically follows one to two days after we have the drawing."
                    : isDemo ? "to schedule your live demo." : "when a seat opens up."}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="auth-stack">
                <div>
                  <div className="auth-title">
                    {isPlans ? "Send us your plan" : isDemo ? "Book a demo" : "Request beta access"}
                  </div>
                  <p className="auth-sub" style={{ marginTop: 6 }}>
                    {isPlans
                      ? "No new software to learn. Tell us about your firm and the project; we reply with where to send your PDF or DWG, and a licensed engineer hands back a stamp-ready plan set and calc package, typically in one to two days."
                      : isDemo
                      ? "Bring your floor plan and we'll run it live. A few questions so we can tailor the demo to how you work."
                      : "Engineering Studio is in private beta for engineering firms. A few questions so we can tailor your access to how you work."}
                  </p>
                </div>

                {/* Progress */}
                <div className="beta-steps">
                  {STEPS.map((label, i) => (
                    <div
                      key={label}
                      className={`beta-step${i === step ? " is-on" : ""}${i < step ? " is-done" : ""}`}
                    >
                      <span className="beta-step-bar" />
                      <span>{i + 1}. {label}</span>
                    </div>
                  ))}
                </div>

                {error && <div className="form-error" role="alert">{error}</div>}

                {/* ---- Step 1 · You ---- */}
                {step === 0 && (
                  <>
                    <label className="field">
                      <span className="field-label">I'm a</span>
                      <div className="seg" role="group" aria-label="Account type">
                        <button
                          type="button"
                          aria-pressed={isFirm}
                          className={`seg-btn${isFirm ? " is-on" : ""}`}
                          onClick={() => set("accountIntent", "firm")}
                        >
                          Firm/Team
                        </button>
                        <button
                          type="button"
                          aria-pressed={!isFirm}
                          className={`seg-btn${!isFirm ? " is-on" : ""}`}
                          onClick={() => set("accountIntent", "individual")}
                        >
                          Designer/Engineer
                        </button>
                      </div>
                    </label>

                    <div className="beta-grid">
                      <label className="field">
                        <span className="field-label">Your name</span>
                        <input
                          type="text" className="field-input" autoComplete="name"
                          value={form.contactName} onChange={(e) => set("contactName", e.target.value)} autoFocus
                        />
                      </label>
                      <label className="field">
                        <span className="field-label">Work email</span>
                        <input
                          type="email" className="field-input" autoComplete="email"
                          value={form.email} onChange={(e) => set("email", e.target.value)} required
                        />
                      </label>
                      <label className="field">
                        <span className="field-label">Role / title <span className="optional">(optional)</span></span>
                        <input
                          type="text" className="field-input" autoComplete="organization-title"
                          placeholder="e.g. Principal, PE"
                          value={form.role} onChange={(e) => set("role", e.target.value)}
                        />
                      </label>
                      <label className="field">
                        <span className="field-label">Phone <span className="optional">(optional)</span></span>
                        <input
                          type="tel" className="field-input" autoComplete="tel"
                          value={form.phone} onChange={(e) => set("phone", e.target.value)}
                        />
                      </label>
                    </div>
                  </>
                )}

                {/* ---- Step 2 · Your firm ---- */}
                {step === 1 && (
                  <div className="beta-grid">
                    <label className="field span-2">
                      <span className="field-label">
                        {isFirm ? "Firm name" : "Practice name"} <span className="optional">(optional)</span>
                      </span>
                      <input
                        type="text" className="field-input" autoComplete="organization"
                        value={form.firmName} onChange={(e) => set("firmName", e.target.value)} autoFocus
                      />
                    </label>

                    <label className="field">
                      <span className="field-label">Discipline <span className="optional">(optional)</span></span>
                      <select
                        className="field-input"
                        value={form.discipline} onChange={(e) => set("discipline", e.target.value)}
                      >
                        <option value="">Select…</option>
                        {DISCIPLINES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </select>
                    </label>

                    {isFirm && (
                      <label className="field">
                        <span className="field-label">Firm size <span className="optional">(optional)</span></span>
                        <select
                          className="field-input"
                          value={form.firmSize} onChange={(e) => set("firmSize", e.target.value)}
                        >
                          <option value="">Select…</option>
                          {FIRM_SIZES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </label>
                    )}

                    <label className="field">
                      <span className="field-label">State / jurisdiction <span className="optional">(optional)</span></span>
                      <input
                        type="text" className="field-input"
                        placeholder="e.g. CA"
                        value={form.jurisdiction} onChange={(e) => set("jurisdiction", e.target.value)}
                      />
                    </label>

                    {isFirm && (
                      <label className="field">
                        <span className="field-label">Seats you'd want <span className="optional">(optional)</span></span>
                        <input
                          type="number" min={1} className="field-input"
                          value={form.seats} onChange={(e) => set("seats", e.target.value)}
                        />
                      </label>
                    )}

                    <label className="field span-2">
                      <span className="field-label">Website <span className="optional">(optional)</span></span>
                      <input
                        type="url" className="field-input" autoComplete="url"
                        placeholder="https://"
                        value={form.website} onChange={(e) => set("website", e.target.value)}
                      />
                    </label>
                  </div>
                )}

                {/* ---- Step 3 · How you work ---- */}
                {step === 2 && (
                  <>
                    <div className="field">
                      <span className="field-label">Which stages do you do? <span className="optional">(optional)</span></span>
                      <div className="chip-group">
                        {STAGES.map((s) => (
                          <button
                            key={s.value} type="button"
                            className={`chip${form.stages.includes(s.value) ? " is-on" : ""}`}
                            onClick={() => toggle("stages", s.value)}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="field">
                      <span className="field-label">What tools do you use? <span className="optional">(optional)</span></span>
                      <div className="chip-group">
                        {TOOLS.map((t) => (
                          <button
                            key={t} type="button"
                            className={`chip${form.tools.includes(t) ? " is-on" : ""}`}
                            onClick={() => toggle("tools", t)}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                      <input
                        type="text" className="field-input" style={{ marginTop: 8 }}
                        placeholder="Other tools (comma-separated)"
                        value={form.otherTools} onChange={(e) => set("otherTools", e.target.value)}
                      />
                    </div>

                    <label className="field">
                      <span className="field-label">
                        {isPlans ? "About the project" : "Your current workflow / SOP"}{" "}
                        <span className="optional">(optional)</span>
                      </span>
                      <textarea
                        className="field-input"
                        placeholder={isPlans
                          ? "Structure type, storeys, site location, and when you need the stamped set."
                          : "How does a project move from intake to stamped deliverable today?"}
                        value={form.currentSop} onChange={(e) => set("currentSop", e.target.value)}
                      />
                    </label>

                    <label className="field">
                      <span className="field-label">
                        {isPlans ? "Anything else we should know?" : "Biggest bottleneck you're hoping to solve"}{" "}
                        <span className="optional">(optional)</span>
                      </span>
                      <textarea
                        className="field-input"
                        value={form.bottleneck} onChange={(e) => set("bottleneck", e.target.value)}
                      />
                    </label>
                  </>
                )}

                {/* ---- Nav ---- */}
                <div className="beta-nav">
                  {step > 0 && (
                    <button type="button" className="btn btn-secondary" onClick={back} disabled={loading}>
                      Back
                    </button>
                  )}
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? "Submitting…" : isLast ? (isPlans ? "Start the intake" : isDemo ? "Book my demo" : "Request access") : "Continue"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <p className="auth-foot">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
        <p className="auth-back">
          <Link to="/">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}
